import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import styled from 'styled-components';
import githubIcon from '@assets/github.svg';
import linkedinIcon from '@assets/linkedin.svg';
import twitterIcon from '@assets/twitter.svg';
const ContactSection = styled.section `
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 32px;
  margin-bottom: 32px;
  position: relative;
  padding-bottom: 32px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 18px;
  }
`;
const ContactLeft = styled.div `
  flex: 1 1 40%;
  display: flex;
  flex-direction: column;
`;
const ContactTitle = styled.h2 `
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0;
  margin-right: 24px;
  align-self: flex-start;

  @media (max-width: 900px) {
    margin-bottom: 12px;
    margin-right: 0;
  }
`;
const SocialIcons = styled.div `
  display: flex;
  gap: 18px;
  margin-top: 18px;
  width: 100%;

  @media (max-width: 900px) {
    justify-content: flex-start;
  }
`;
const SocialLink = styled.a `
  transition: filter 0.2s;

  img {
    width: 28px;
    height: 28px;
    transition: filter 0.2s;
  }

  &:hover img {
    filter: none;
  }
`;
const ContactForm = styled.form `
  flex: 1 1 60%;
  display: flex;
  gap: 12px;
  align-items: center;
  flex-direction: column;

  @media (max-width: 900px) {
    gap: 8px;
    width: 100%;
  }
`;
const FormInput = styled.input `
  flex: 1;
  padding: 10px 12px;
  border: 1.5px solid ${props => props.$hasError ? '#ff4444' : '#eee'};
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  transition: border 0.2s;
  width: 380px;

  &:focus {
    border: 1.5px solid ${props => props.$hasError ? '#ff4444' : '#ffce3c'};
  }

  @media (max-width: 900px) {
    width: 100%;
  }
`;
const FormTextarea = styled.textarea `
  flex: 1;
  padding: 10px 12px;
  border: 1.5px solid ${props => props.$hasError ? '#ff4444' : '#eee'};
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  transition: border 0.2s;
  width: 380px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    border: 1.5px solid ${props => props.$hasError ? '#ff4444' : '#ffce3c'};
  }

  @media (max-width: 900px) {
    width: 100%;
  }
`;
const SubmitButton = styled.button `
  background: ${props => props.$isSubmitting ? '#ccc' : '#ffce3c'};
  color: #111;
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.$isSubmitting ? 'not-allowed' : 'pointer'};
  transition: background 0.2s;
  width: 407px;
  margin: 0;

  &:hover {
    background: ${props => props.$isSubmitting ? '#ccc' : '#111'};
    color: ${props => props.$isSubmitting ? '#111' : '#fff'};
  }

  @media (max-width: 900px) {
    width: 100%;
  }
`;
const ErrorMessage = styled.span `
  color: #ff4444;
  font-size: 0.85rem;
  margin-top: -8px;
  margin-bottom: 4px;
  align-self: flex-start;
  width: 380px;

  @media (max-width: 900px) {
    width: 100%;
  }
`;
const SuccessMessage = styled.div `
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  width: 380px;

  @media (max-width: 900px) {
    width: 100%;
  }
`;
const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        }
        else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }
        if (!formData.message.trim()) {
            newErrors.message = 'Message is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        setIsSubmitting(true);
        try {
            // Environment-aware API endpoint
            // Development: Uses Express server (localhost:3001) via webpack proxy
            // Production: Uses Firebase Functions
            const apiUrl = process.env.NODE_ENV === 'development'
                ? '/api/contact' // Webpack proxy routes to localhost:3001
                : 'https://us-central1-raja-portfolio-dad3c.cloudfunctions.net/api/contact'; // Direct Functions URL
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });
            const data = await response.json();
            if (response.ok && data.success) {
                setIsSubmitted(true);
                setFormData({ name: '', email: '', message: '' });
                setTimeout(() => setIsSubmitted(false), 5000); // Hide success message after 5 seconds
            }
            else {
                throw new Error(data.error || 'Failed to send message');
            }
        }
        catch (error) {
            console.error('Error sending message:', error);
            alert('Failed to send message. Please try again.');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    return (_jsxs(ContactSection, { children: [_jsxs(ContactLeft, { children: [_jsx(ContactTitle, { children: "Let's build something amazing together." }), _jsxs(SocialIcons, { children: [_jsx(SocialLink, { href: "https://github.com/rajakedia", target: "_blank", rel: "noopener noreferrer", "aria-label": "GitHub", children: _jsx("img", { src: githubIcon, alt: "GitHub" }) }), _jsx(SocialLink, { href: "https://linkedin.com/in/rajakedia", target: "_blank", rel: "noopener noreferrer", "aria-label": "LinkedIn", children: _jsx("img", { src: linkedinIcon, alt: "LinkedIn" }) }), _jsx(SocialLink, { href: "https://twitter.com/rajakedia", target: "_blank", rel: "noopener noreferrer", "aria-label": "Twitter", children: _jsx("img", { src: twitterIcon, alt: "Twitter" }) })] })] }), _jsxs(ContactForm, { onSubmit: handleSubmit, children: [isSubmitted && (_jsx(SuccessMessage, { children: "Thank you! Your message has been sent successfully." })), _jsx(FormInput, { type: "text", name: "name", placeholder: "Name", value: formData.name, onChange: handleInputChange, "$hasError": !!errors.name, required: true }), errors.name && _jsx(ErrorMessage, { children: errors.name }), _jsx(FormInput, { type: "email", name: "email", placeholder: "Email", value: formData.email, onChange: handleInputChange, "$hasError": !!errors.email, required: true }), errors.email && _jsx(ErrorMessage, { children: errors.email }), _jsx(FormTextarea, { name: "message", placeholder: "Message", value: formData.message, onChange: handleInputChange, "$hasError": !!errors.message, required: true }), errors.message && _jsx(ErrorMessage, { children: errors.message }), _jsx(SubmitButton, { type: "submit", "$isSubmitting": isSubmitting, disabled: isSubmitting, children: isSubmitting ? 'Sending...' : 'Send' })] })] }));
};
export default Contact;
